// Aggregation helpers for the workflow detail page's Results panel.
//
// Workers record a JSON counters payload on every Job they run. For
// export Jobs the shape is:
//   {
//     bytes_written: number,
//     cpu_seconds: number,
//     memory_gib_seconds: number,
//     pages_matched_metadata: number,    // matched the filter
//     pages_filtered_search: number,     // dropped by Lucene search
//     pages_skipped_no_transcript: number,
//     fetch_failures: number,
//     pages_exported: number,            // actually written to zip
//     zips_written: number,
//   }
//
// A batch_export workflow has one Job per month. We sum across them so
// the Results panel can show the full-workflow story rather than a
// per-month spreadsheet.

import { WorkflowDetail, WorkflowJob } from "./workflows";

interface RawCounters {
  bytes_written?: number;
  cpu_seconds?: number;
  memory_gib_seconds?: number;
  pages_matched_metadata?: number;
  pages_filtered_search?: number;
  pages_skipped_no_transcript?: number;
  fetch_failures?: number;
  pages_exported?: number;
  // Pages decompressed by the inflate workers (== pages_exported when
  // no content filter; > pages_exported when a regex/lucene query
  // gates some pages out). Lets the UI render input-pps next to
  // output-pps so the user sees the work that didn't make it into
  // the output.
  pages_decompressed?: number;
  zips_written?: number;
  // Coalescing telemetry. One ranged GET per chunk; without
  // coalescing we'd have done one per page. The ratio tells us how
  // well the per-zip grouping is packing.
  s3_get_requests?: number;
  s3_get_bytes_fetched?: number;
  // Sum of compressed-payload bytes for entries we actually wanted.
  // Difference vs s3_get_bytes_fetched is the gap-skip waste between
  // selected entries — free since intra-region bandwidth doesn't
  // bill, but a useful read on cluster density.
  s3_get_bytes_useful?: number;
  // Sum of total sizes of every source zip we touched at least once.
  // bytes_fetched is what we actually pulled; the difference
  // (baseline − fetched) is the bigger savings — bytes that exist in
  // the source zip but that the metadata filter ruled out before any
  // S3 read happened.
  s3_zip_baseline_bytes?: number;
}

export interface ExportSummary {
  // Funnel: input → kept → dropped breakdown.
  pagesMatched: number; // total that passed the metadata filter
  pagesExported: number; // actually written to a zip
  pagesDecompressed: number; // inflate-stage count (>= pagesExported)
  pagesFilteredBySearch: number; // Lucene search dropped them
  pagesSkippedNoTranscript: number; // empty or unparseable XML
  pagesFetchFailures: number; // S3 GET failures
  // Output volume.
  bytesWritten: number;
  zipsWritten: number;
  // Compute (also available via cost.ts but we surface a raw seconds
  // figure here for the "how much work went into this" UX).
  cpuSeconds: number;
  memoryGibSeconds: number;
  // Wall-clock duration in ms (finished_at - started_at, or null
  // until both timestamps land).
  wallMs: number | null;
  // S3 efficiency. s3GetRequests is what we'd be billed for; the
  // "naive" baseline (one GET per metadata-matched page) is
  // pagesMatched, so the savings are pagesMatched - s3GetRequests.
  // s3BytesFetched is the raw download (gaps included);
  // s3BytesSkipped is the gap-waste portion (bytes we pulled but
  // immediately discarded because they sat between the entries we
  // actually wanted in a coalesced span);
  // s3BytesAvoided is the bigger bandwidth savings — bytes that
  // exist in the source zips we touched but that the metadata
  // filter ruled out *before* any S3 read happened. All three are
  // diagnostic only since intra-region traffic to EKS is free.
  s3GetRequests: number;
  s3BytesFetched: number;
  s3BytesSkipped: number;
  s3BytesAvoided: number;
  // True if at least one export Job has counters data populated. Used
  // by the page to decide whether the Results panel can render at all.
  hasData: boolean;
}

function parseCounters(j: WorkflowJob): RawCounters {
  if (!j.counters_json) return {};
  try {
    return JSON.parse(j.counters_json) as RawCounters;
  } catch {
    return {};
  }
}

const EXPORT_JOB_KINDS = new Set(["export"]);

export function summarizeExport(wf: WorkflowDetail): ExportSummary {
  const exportJobs = wf.jobs.filter((j) => EXPORT_JOB_KINDS.has(j.kind));
  const counters = exportJobs.map(parseCounters);
  const sum = (key: keyof RawCounters) =>
    counters.reduce((acc, c) => acc + (c[key] ?? 0), 0);

  // wallMs is "started_at → finished_at" when the run completed; for
  // in-progress runs (finished_at null but started_at set) it's
  // "started_at → now" so the Duration tile is live during the run.
  // ExportDetail polls every 5s, so the value advances at that
  // cadence — fine for a duration display.
  const wallMs = wf.started_at
    ? (wf.finished_at ?? Date.now()) - wf.started_at
    : null;

  return {
    pagesMatched: sum("pages_matched_metadata"),
    pagesExported: sum("pages_exported"),
    pagesDecompressed: sum("pages_decompressed"),
    pagesFilteredBySearch: sum("pages_filtered_search"),
    pagesSkippedNoTranscript: sum("pages_skipped_no_transcript"),
    pagesFetchFailures: sum("fetch_failures"),
    bytesWritten: sum("bytes_written"),
    zipsWritten: sum("zips_written"),
    cpuSeconds: sum("cpu_seconds"),
    memoryGibSeconds: sum("memory_gib_seconds"),
    wallMs,
    s3GetRequests: sum("s3_get_requests"),
    s3BytesFetched: sum("s3_get_bytes_fetched"),
    s3BytesSkipped: Math.max(
      sum("s3_get_bytes_fetched") - sum("s3_get_bytes_useful"),
      0,
    ),
    s3BytesAvoided: Math.max(
      sum("s3_zip_baseline_bytes") - sum("s3_get_bytes_fetched"),
      0,
    ),
    hasData: counters.some((c) => Object.keys(c).length > 0),
  };
}

// --- formatting -------------------------------------------------------

// Compact UTC stamp: YYYY-MM-DD HH:MM:SSZ. Local time would surprise
// ops who think in UTC; mirror what the rest of the SPA shows.
export function formatTs(ms: number | null | undefined): string {
  if (ms == null) return "—";
  return new Date(ms).toISOString().replace("T", " ").slice(0, 19) + "Z";
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

export function formatDuration(ms: number | null): string {
  if (ms === null || ms < 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs === 0 ? `${m}m` : `${m}m ${rs}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm === 0 ? `${h}h` : `${h}h ${rm}m`;
}

// Compact "1.2k" / "1.3M" / "303k" — used for big page counts that
// don't need the full thousands-separated number on the headline.
export function formatCount(n: number): string {
  if (n < 1000) return n.toLocaleString();
  if (n < 10_000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  if (n < 10_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

// --- ETA --------------------------------------------------------------
//
// Cumulative-average rate from started_at + progress_completed. Slightly
// pessimistic during ramp-up (process-pool spawn + first-batch fetch
// stall the first ~30s at zero pages/s), but for a 30+ minute job the
// distortion is small. The polling cadence (~5s) and inputs we have
// don't really support a trailing-window estimate without extra UI
// state, and keeping the formula stateless lets the React render be a
// pure function of the polled job dict.

export interface JobEta {
  // Pages-per-second rate, derived from progress_completed/elapsed.
  ratePagesPerSec: number;
  // Milliseconds remaining at the current rate. null when we can't
  // compute (no progress yet, or already done, or no started_at).
  remainingMs: number | null;
}

export function estimateJobEta(
  job: {
    started_at: number | null;
    finished_at: number | null;
    progress_completed: number | null;
    progress_total: number | null;
    status: string;
  },
  nowMs: number = Date.now(),
): JobEta {
  if (
    !job.started_at ||
    job.finished_at ||
    job.status !== "running" ||
    !job.progress_total ||
    job.progress_total <= 0 ||
    !job.progress_completed ||
    job.progress_completed <= 0
  ) {
    return { ratePagesPerSec: 0, remainingMs: null };
  }
  const elapsedMs = nowMs - job.started_at;
  if (elapsedMs <= 0) return { ratePagesPerSec: 0, remainingMs: null };
  const rate = job.progress_completed / (elapsedMs / 1000);
  if (rate <= 0) return { ratePagesPerSec: 0, remainingMs: null };
  const remaining = job.progress_total - job.progress_completed;
  if (remaining <= 0) {
    return { ratePagesPerSec: rate, remainingMs: 0 };
  }
  return { ratePagesPerSec: rate, remainingMs: (remaining / rate) * 1000 };
}
